import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink, RefreshCw, Link2, Trash2, Tag, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, inr } from "../../../lib/api";
import { initials } from "./util";

const STATUS_CLS = { paid: "bg-emerald-100 text-emerald-700", completed: "bg-emerald-100 text-emerald-700", partial: "bg-amber-100 text-amber-700" };

function RegCard({ r, canLink, onLink }) {
  const navigate = useNavigate();
  const resend = async () => {
    try { await api.post(`/whatsapp/resend/${r.id}`); toast.success("Confirmation re-sent on WhatsApp"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-1.5 text-sm" data-testid={`inbox-reg-${r.registration_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-[#111b21] truncate">{r.student_name || "—"}</span>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_CLS[r.payment_status] || "bg-slate-100 text-slate-600"}`}>{(r.payment_status || "").replace(/_/g, " ")}</span>
      </div>
      <div className="text-xs text-[#667781] space-y-0.5">
        <div className="font-mono">{r.registration_id}</div>
        {r.parent_name && <div>Parent: {r.parent_name}</div>}
        {r.school && <div>{r.school}</div>}
        <div>{[r.program, r.level, r.batch].filter(Boolean).join(" · ")}</div>
        <div>Fee {inr(r.amount)}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        <button onClick={() => navigate(`/admin/registrations/${r.id}`)} className="text-xs px-2 py-1 rounded-md bg-[#f0f2f5] hover:bg-[#e9edef] flex items-center gap-1" data-testid="inbox-reg-open-btn"><ExternalLink className="w-3 h-3" /> Open</button>
        <button onClick={resend} className="text-xs px-2 py-1 rounded-md bg-[#f0f2f5] hover:bg-[#e9edef] flex items-center gap-1" data-testid="inbox-reg-resend-btn"><RefreshCw className="w-3 h-3" /> Resend confirmation</button>
        {r.page_slug && <button onClick={() => onLink(`${window.location.origin}/r/${r.page_slug}`)} disabled={!canLink} title={canLink ? "" : "Reply window closed"} className="text-xs px-2 py-1 rounded-md bg-[#f0f2f5] hover:bg-[#e9edef] disabled:opacity-50 flex items-center gap-1" data-testid="inbox-reg-paylink-btn"><Link2 className="w-3 h-3" /> Payment link</button>}
      </div>
    </div>
  );
}

function Notes({ conv, onChange }) {
  const [text, setText] = useState("");
  const add = async () => {
    try { const { data } = await api.post(`/wa-inbox/conversations/${conv.phone}/notes`, { text }); setText(""); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#667781] flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Private notes</div>
      {(conv.notes || []).map((n) => (
        <div key={n.id} className="bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 text-xs group" data-testid="inbox-note">
          <div className="whitespace-pre-wrap text-[#111b21]">{n.text}</div>
          <div className="flex justify-between text-[10px] text-[#667781] mt-0.5">{n.by} · {new Date(n.at).toLocaleString("en-IN")}
            <button onClick={() => api.delete(`/wa-inbox/conversations/${conv.phone}/notes/${n.id}`).then(({ data }) => onChange(data))} className="opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button></div>
        </div>
      ))}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a note (parent never sees this)" className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="inbox-note-input" />
      <button onClick={add} disabled={!text.trim()} className="w-full text-sm bg-[#00a884] disabled:opacity-50 text-white rounded-md py-1.5" data-testid="inbox-note-add-btn">Add note</button>
    </div>
  );
}

function Labels({ conv, onChange }) {
  const [v, setV] = useState("");
  const save = (labels) => api.patch(`/wa-inbox/conversations/${conv.phone}`, { labels }).then(({ data }) => onChange(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#667781] flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Labels</div>
      <div className="flex flex-wrap gap-1.5">
        {(conv.labels || []).map((l) => <span key={l} className="text-xs bg-emerald-50 text-emerald-700 rounded-full pl-2 pr-1 py-0.5 flex items-center gap-1" data-testid={`inbox-label-${l}`}>{l}<button onClick={() => save(conv.labels.filter((x) => x !== l))}><X className="w-3 h-3" /></button></span>)}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (v.trim()) { save([...(conv.labels || []), v.trim()]); setV(""); } }} className="flex gap-1.5">
        <input value={v} onChange={(e) => setV(e.target.value)} list="inbox-label-opts" placeholder="Payment issue, Query, Refund…" className="flex-1 text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="inbox-label-input" />
        <datalist id="inbox-label-opts">{["Payment issue", "Query", "Refund", "Admission", "Resolved"].map((o) => <option key={o} value={o} />)}</datalist>
        <button className="text-sm px-3 rounded-md bg-[#f0f2f5]" data-testid="inbox-label-add-btn">Add</button>
      </form>
    </div>
  );
}

export function ProfilePanel({ conv, onClose, onConvChange, onInsertLink }) {
  const [regs, setRegs] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    setRegs(null);
    api.get(`/wa-inbox/conversations/${conv.phone}/profile`).then(({ data }) => setRegs(data.registrations)).catch(() => setRegs([]));
  }, [conv.phone]);
  useEffect(() => { api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const assign = (email) => api.patch(`/wa-inbox/conversations/${conv.phone}`, { assigned_to: email }).then(({ data }) => { onConvChange(data); toast.success(email ? "Chat assigned" : "Unassigned"); });
  return (
    <div className="h-full flex flex-col bg-white border-l border-slate-200" data-testid="inbox-profile-panel">
      <div className="h-[60px] px-4 bg-[#f0f2f5] flex items-center gap-3 flex-shrink-0">
        <button onClick={onClose} data-testid="inbox-profile-close-btn"><X className="w-5 h-5 text-[#54656f]" /></button>
        <span className="font-medium text-[#111b21]">Contact info</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#dfe5e7] text-[#54656f] flex items-center justify-center text-2xl font-semibold">{initials(conv.display_name)}</div>
          <div className="mt-2 font-semibold text-[#111b21]">{conv.display_name}</div>
          <div className="text-sm text-[#667781]">+{conv.phone}</div>
          {conv.profile_name && conv.profile_name !== conv.display_name && <div className="text-xs text-[#667781]">WhatsApp name: ~{conv.profile_name}</div>}
        </div>
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#667781]">Assigned to</div>
          <select value={conv.assigned_to || ""} onChange={(e) => assign(e.target.value)} className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="inbox-assign-select">
            <option value="">Unassigned</option>
            {users.map((u) => <option key={u.id} value={u.email}>{u.name || u.email}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#667781]">Registrations</div>
          {regs === null && <div className="text-xs text-[#667781]">Loading…</div>}
          {regs?.length === 0 && <div className="text-xs text-[#667781]">No registration found for this number.</div>}
          {regs?.map((r) => <RegCard key={r.id} r={r} canLink={conv.window_open} onLink={onInsertLink} />)}
        </div>
        <Labels conv={conv} onChange={onConvChange} />
        <Notes conv={conv} onChange={onConvChange} />
      </div>
    </div>
  );
}
